/**
 * mpwa Video Player — VAST 4.0 InLine + AdPod player.
 *
 * Browser usage (IIFE bundle):
 *
 *   <script src="https://mpwa.to/sdk/v1/video-player.min.js"></script>
 *   <div id="my-slot" style="width:640px"></div>
 *   <script>
 *     MpwaVideoPlayer.play({
 *       container: document.getElementById('my-slot'),
 *       url: 'https://ads.mpwa.to/v1/ad/vast?ad_unit_id=42',
 *       skipAfter: 5,
 *     });
 *   </script>
 *
 * Or via data attributes for auto-init:
 *
 *   <div data-mpwa-video-ad data-vast-url="https://ads.mpwa.to/v1/ad/vast?ad_unit_id=42"
 *        data-skip-after="5" data-locale="en"></div>
 *
 * Bundle target: < 50 KB gzipped (vanilla, no deps).
 */
import { playAd } from './player.js';
import { parseVast } from './vast.js';
import { initAuto } from './auto.js';
import type { ParsedAd, PlayerHandle, PlayerOptions } from './types.js';

export const VERSION = '0.1.0';

export interface PlayResult {
  /** Skip imperatively. */
  skip(): void;
  /** Destroy / tear down. */
  destroy(): void;
  /** Resolves when all ads in the pod have completed or been skipped. */
  done: Promise<void>;
}

/**
 * Fetch + parse VAST, then play the InLine ad (or pod) sequentially.
 * If `xml` is provided it's used directly; otherwise `url` is fetched.
 */
export async function play(opts: PlayerOptions): Promise<PlayResult> {
  let xml = opts.xml;
  if (!xml && opts.url) {
    const res = await fetch(opts.url, { credentials: 'include' });
    if (!res.ok) throw new Error(`VAST fetch failed: ${res.status}`);
    xml = await res.text();
  }
  if (!xml) throw new Error('PlayerOptions requires either xml or url');

  const ads: ParsedAd[] = parseVast(xml);
  if (ads.length === 0) throw new Error('VAST contained no playable Ad');

  let current: (PlayerHandle & { done: Promise<void> }) | null = null;
  let destroyed = false;
  const overallDone = (async (): Promise<void> => {
    for (const ad of ads) {
      if (destroyed) return;
      current = playAd(ad, opts);
      try {
        await current.done;
      } catch (err) {
        opts.onError?.(err instanceof Error ? err : new Error(String(err)));
        // continue with the next ad in the pod rather than dropping it
      }
    }
    opts.onAllAdsCompleted?.();
  })();

  return {
    skip(): void {
      current?.skip();
    },
    destroy(): void {
      destroyed = true;
      current?.destroy();
    },
    done: overallDone,
  };
}

// Auto-init on DOMContentLoaded — picks up [data-mpwa-video-ad] elements.
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAuto(play));
  } else {
    queueMicrotask(() => initAuto(play));
  }
}

// Re-exports for ESM consumers.
export { parseVast, selectMedia } from './vast.js';
export type { ParsedAd, MediaFile, CompanionAd, PlayerOptions, VastEvent } from './types.js';
